// antd 5 theme from the 2way design system (github.com/2waymessage/design-system).
import { theme as antdTheme } from 'antd';

const shared = {
  fontFamily: "'Afacad Flux', Arial, sans-serif",
  borderRadius: 12, // radius-lg: buttons, inputs, cards
  borderRadiusLG: 12, // radius-lg
  borderRadiusSM: 6, // radius-sm
  colorPrimary: '#ff6a00', // color-primary
  colorPrimaryHover: '#ff964c', // color-primary-hover
  colorPrimaryActive: '#cc5400', // orange-dark
  colorError: '#ff5757', // color-danger
  colorSuccess: '#22c55e', // color-success
  colorInfo: '#3b82f6', // color-blue
};

const light = {
  ...shared,
  colorLink: '#1d4ed8', // color-blue-dark: links are blue (orange text on white is 2.9:1)
  colorLinkHover: '#3b82f6',
  colorText: '#1a1a1a', // color-text-primary
  colorTextSecondary: '#666666', // color-text-secondary
  colorErrorText: '#cc3232', // color-danger-dark
  colorSuccessText: '#15803d', // color-success-dark
  colorBorder: '#dddddd', // color-border
  colorBorderSecondary: '#eeeeee', // color-track
  colorBgLayout: '#f1f1f1', // color-bg
  colorBgContainer: '#ffffff', // color-surface
  colorFillAlter: '#f5f5f5', // color-surface-sunken
};

const dark = {
  ...shared,
  colorLink: '#60a5fa', // color-blue-dark (dark)
  colorLinkHover: '#93c5fd',
  colorText: '#ffffff', // color-text-primary
  colorTextSecondary: '#939393', // color-text-secondary
  colorErrorText: '#ff5757', // color-danger-dark (dark)
  colorSuccessText: '#22c55e', // color-success-dark (dark)
  colorBorder: 'rgba(255, 255, 255, 0.15)', // color-border (white-a15)
  colorBorderSecondary: 'rgba(255, 255, 255, 0.1)', // color-track
  colorBgLayout: '#1a1a1a', // color-bg
  colorBgContainer: '#282828', // color-surface
  colorBgElevated: '#282828',
  colorFillAlter: '#1e1e1e', // color-surface-sunken
};

// Buttons: label on orange is always black (6.1:1; white is 2.9:1), Medium weight; the
// secondary button turns color-primary-light on hover.
const buttons = {
  primaryColor: '#1a1a1a', // color-text-on-primary
  fontWeight: 500,
  primaryShadow: 'none',
  defaultShadow: 'none',
  dangerShadow: 'none',
};

// The sidebar is dark in both modes.
const sidebar = {
  Menu: {
    darkItemBg: '#1a1a1a', // black
    darkSubMenuItemBg: '#141414',
    darkItemColor: '#b2b2b2', // neutral-light
    darkItemHoverColor: '#ffffff',
    darkItemHoverBg: 'rgba(255, 255, 255, 0.05)',
    darkItemSelectedBg: 'rgba(255, 255, 255, 0.10)', // white-a10
    darkItemSelectedColor: '#ff6a00', // color-primary, 6.1:1 on #1a1a1a
    itemBorderRadius: 12,
    itemMarginInline: 8,
    iconSize: 16,
    collapsedIconSize: 18,
  },
  Layout: {
    siderBg: '#1a1a1a',
    triggerBg: '#282828',
  },
};

// Embedded in Shoper Connect (always light): Shoper's font, blue accent and text colours.
const shoper = {
  ...light,
  fontFamily: "'Source Sans Pro', Arial, sans-serif",
  colorPrimary: '#3c83ec',
  colorPrimaryHover: '#135cc8',
  colorPrimaryActive: '#135cc8',
  colorLink: '#135cc8',
  colorLinkHover: '#3c83ec',
  colorText: '#2d3748',
  colorTextSecondary: '#5c657e',
  colorBgLayout: '#f3f4f8',
  borderRadius: 4,
  borderRadiusLG: 6,
  borderRadiusSM: 4,
  colorErrorText: '#cc3232',
};

const shoperComponents = {
  Menu: {
    ...sidebar.Menu,
    darkItemBg: '#49456c', // Shoper Connect's purple navigation
    darkSubMenuItemBg: '#403c60',
    darkItemColor: '#e1e6f3',
    darkItemSelectedColor: '#ffffff',
    darkItemSelectedBg: 'rgba(255, 255, 255, 0.12)',
    itemBorderRadius: 6,
  },
  Layout: {siderBg: '#49456c', triggerBg: '#403c60'},
  Button: {borderRadius: 20, controlHeight: 36, fontWeight: 600, primaryColor: '#ffffff', primaryShadow: 'none'},
  Tabs: {itemSelectedColor: '#2d3748', inkBarColor: '#3c83ec'},
};

export const getDesignTheme = (mode, shoperApp = false) => (shoperApp ? {
  algorithm: antdTheme.defaultAlgorithm,
  token: shoper,
  components: shoperComponents,
} : {
  algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  token: mode === 'dark' ? dark : light,
  components: {...sidebar, Button: {...buttons, ...(mode === 'dark' ? {} : {defaultHoverBg: '#ffe9d9'})}},
});
